<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      v-model="selectedAnalysis"
      :items="displayAnalysis"
      :isSearchable="false"
      :headers="tableHeaders"
      :disablePagination="true"
      :itemActions="actions"
      :pagination="pagination"
      :isCustomIcon="true"
      hideDefaultFooter
      showSelect
      :withCustomSelect="true"
      :isLabAnalysisTemplate="true"
      :isInfinitScroll="true"
      :title="$t('laboratory.dataTransfer')"
      :sortableColumns="sortableColumns"
      :centerColumns="['date']"
      :loading="loading"
      @sendQueryParams="fetchQuery"
      @iconAction="onClickAction"
      @type-changed="typeChanged"
    >
      <template v-slot:[`item.mainContact`]="{ item }">
        <div class="date d-flex flex-row justify-start">
          <div class="d-flex flex-column align-baseline">
            <span v-if="item.mainContact">{{ $t(`contacts.labels.email`) }} :</span>
            <span v-if="item.mainContactPhoneNumber">{{ $t(`forms.labels.phone`) }} :</span>
          </div>
          <div class="d-flex flex-column justify-start">
            <span v-if="item.mainContact"> {{ item.mainContact }}</span>
            <span v-if="item.mainContactPhoneNumber"> {{ item.mainContactPhoneNumber }} </span>
          </div>
        </div>
      </template>
      <template v-slot:[`item.date`]="{ item }">
        <div class="date d-flex flex-row justify-space-around">
          <div class="d-flex flex-column align-baseline">
            <span>{{ $t(`laboratory.labels.sampling`) }}:</span>
            <span>{{ $t(`laboratory.labels.analysisName`) }}:</span>
            <span>{{ $t(`laboratory.labels.report`) }}:</span>
          </div>
          <div class="d-flex flex-column">
            <span> {{ formatDate(item.sampleadAt) }}</span>
            <span> {{ formatDate(item.analysedAt) }}</span>
            <span> {{ formatDate(item.reportedAt) }}</span>
          </div>
        </div>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span>{{ item.status.name }}</span>
      </template>
      <template #extraHeader>
        <div class="download-container">
          <v-btn
            color="secondary btn-download"
            class="m-2"
            @click.stop="generateXML"
          >
            <v-icon>
              save_alt
            </v-icon>
            {{ $t('laboratory.labels.downloadXML') }}
          </v-btn>
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :datesRange="true"
          :saveFilter="true"
          :filterName="'dataTransfer'"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
        <div class="m-2">
          <!--  ON HOLD -->
          <v-btn
            small
            color="secondary"
            class="siteTree-btn"
            data-cy="datatransfer-onHoldButton"
            :disabled="!selectedAnalysis.length"
            @click.stop="changeAnalysisStatusMultipleItems('WAITING')"
          >
            <span> {{ $t('laboratory.labels.waiting') }}</span>
          </v-btn>
          <!-- Add to file - Included to file -->
          <v-btn
            small
            color="secondary"
            class="siteTree-btn"
            data-cy="datatransfer-addToFile-Button"
            :disabled="!selectedAnalysis.length"
            @click.stop="changeAnalysisStatusMultipleItems('TODO')"
          >
            <span> {{ $t('laboratory.labels.putback') }}</span>
          </v-btn>
          <v-btn
            small
            color="secondary"
            class="siteTree-btn"
            data-cy="datatransfer-removeFromFile-Button"
            :disabled="!selectedAnalysis.length"
            @click.stop="changeAnalysisStatusMultipleItems('REMOVED')"
          >
            <span> {{ $t('laboratory.labels.removeFromFile') }}</span>
          </v-btn>
        </div>
      </template>
    </VTableRemote>
  </div>
</template>
<script>

import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import { debounce, isEmpty } from 'lodash';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';

export default {
    name: 'SamplesRegister',
    components: {
        VTableRemote,
        SearchWithFilters,

    },
    data() {
        return {
            labAnalysis: [],
            isInitializing: false,
            loading: false,
            pagination: { },
            actualRecord: { totalElementCount: 0 },
            queryParams: { perpage: 25 },
            selectedAnalysis: [],
            componentTypes: [],
            provinces: [],
            debouceLoadData: debounce(this.getLabAnalysis, 500),

        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        headers() {
            return [
                'reportNumber',
                'siteNameAndNumber',
                'address',
                'province',
                'mainContact',
                'sampleNumber',
                'componentName',
                'regulatoryId',
                'date',
                'status',
                'analysis',
                'result',
            ];
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`laboratory.labels.${header}`),
                width: header === 'address' ? '200px' : '100px',
                value: header,
            }));
        },
        displayAnalysis() {
            return this.labAnalysis.length ? this.labAnalysis.map(analysis => ({
                ...analysis,
                siteNameAndNumber: `${analysis.siteName} / ${analysis.siteNumber}`,
                componentName: analysis.componentTypeName[this.userLang] || analysis.componentTypeName[this.teamMainLang],
                status: {
                    key: analysis.status,
                    name: this.$t(`laboratory.labels.${analysis.status.toLowerCase()}`),
                },
                isSelectable: analysis.status !== 'HOLD',
            })) : [];
        },
        getComponents() {
            return this.componentTypes.length ? this.componentTypes
                .map(cp => ({
                    value: cp.id,
                    text: cp.name[this.userLang] || cp.name[this.teamMainLang],
                })) : [];
        },
        isMultipleSelected() {
            return this.selectedAnalysis.length > 0;
        },
        actions() {
            const actions = [];

            actions.push({
                name: this.$t('laboratory.labels.removeFromFile'),
                icon: 'close',
                mode: 'close',
                iconAndTextColor: '#26384B',
            });
            actions.push({
                name: this.$t('laboratory.labels.waiting'),
                icon: 'hourglass_empty',
                mode: 'waiting',
                iconAndTextColor: '#26384B',
            });
            actions.push({
                name: this.$t('laboratory.labels.putback'),
                icon: 'input',
                mode: 'input',
                iconAndTextColor: '#26384B',
            });
            actions.push({
                name: this.$t('laboratory.labels.update'),
                icon: 'update',
                mode: 'update',
                iconAndTextColor: '#26384B',
            });
            actions.push({
                name: this.$t('forms.btn.delete'),
                icon: 'delete',
                mode: 'delete',
                iconAndTextColor: '#EF4135',
            });
            return actions;
        },

        sortableColumns() {
            return this.headers.filter(h => h !== 'date').map(column => this.$options.filters.lowercase(column));
        },

        filters() {
            const filtersList = [
                {
                    key: 'components',
                    label: 'forms.labels.componentType',
                    type: 'select',
                    value: [],
                    multiple: true,
                    items: this.componentTypes.length ? this.componentTypes
                        .map(cp => ({
                            value: cp.id,
                            text: cp.name[this.userLang] || cp.name[this.teamMainLang],
                            isAlreadyTranslated: true,
                        })) : [],
                    datacy: 'dataTransfer_componentType',
                },
                {
                    key: 'province',
                    label: 'forms.labels.province',
                    type: 'select',
                    value: [],
                    multiple: false,
                    items: this.provinces && this.provinces ? this.provinces.map(province => ({
                        text: province,
                        value: province,
                        isAlreadyTranslated: true,
                    })) : [],
                    datacy: 'dataTransfer_province',
                },
                {
                    key: 'type',
                    label: 'forms.labels.filterBy',
                    type: 'select',
                    value: ['HOLD', 'TODO'],
                    multiple: true,
                    items: [
                        {
                            value: 'TODO',
                            text: 'laboratory.labels.todo',
                        }, {
                            value: 'WAITING',
                            text: 'laboratory.labels.waiting',
                        },
                        {
                            value: 'REMOVED',
                            text: 'laboratory.labels.removed',
                        },
                        {
                            value: 'SENT',
                            text: 'laboratory.labels.sent',
                        },
                        {
                            value: 'HOLD',
                            text: 'laboratory.labels.hold',
                        },
                    ],
                    datacy: 'dataTransfer_filterBy',
                },


            ];
            return filtersList;
        },
    },
    async mounted() {
        window.onscroll = () => {
            if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) {
                if (this.labAnalysis.length < this.pagination.totalElementCount) {
                    this.queryParams = {
                        ...this.queryParams,
                        page: this.pagination.currentPage + 1,
                    };
                    this.debouceLoadData();
                }
            }
        };
    },
    async created() {
        this.isInitializing = true;
        await this.getLabAnalysis();
        await this.getProvinces();
        await this.getComponentType();
        this.isInitializing = false;
    },
    methods: {
        async getLabAnalysis() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                this.loading = true;
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                const { data } = await this.$rq.getLabAnalysis(cleanQueryParams);
                this.labAnalysis = [...this.labAnalysis, ...data.labAnalysis];
                this.pagination = data.pagination;
                this.loading = false;
            }
        },
        async getProvinces() {
            const { data } = await this.$rq.getLabAnalysisProvinces();
            this.provinces = data.provinces;
        },
        async getComponentType() {
            const { data } = await this.$rq.getLabAnalysisComponentType();
            this.componentTypes = data.componentTypes;
        },
        async fetchQuery(queryParams) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,

                };
                this.pagination = {};
                this.labAnalysis = [];


                this.debouceLoadData();
            }
        },
        formatDate(dt) {
            return dt ? this.$options.filters.formatDate(dt, 'L LTS') : '';
        },
        onClickAction(item, action) {
            let status;
            switch (action.mode) {
                case 'close':
                    status = 'REMOVED';
                    break;
                case 'waiting':
                    status = 'WAITING';
                    break;
                case 'input':
                    status = 'TODO';
                    break;
                case 'update':
                    this.updateAnalysisInfo(item);
                    break;
                case 'delete':
                    this.deleteAnalysis(item);
                    break;
                default:
                    break;
            }
            if (status) {
                this.changeAnalysisStatus([item.id], status);
            }
        },
        typeChanged(value) {
            this.queryParams = { ...this.queryParams, type: value };
            this.debouceLoadData();
        },
        async changeAnalysisStatus(analysis, status) {
            const payload = {
                analysis,
                status,
            };
            await this.$rq.changeLabAnalysisStatus(payload);
            this.$toasted.global.toast_success({
                message: this.$t('laboratory.sucess'),
            });

            this.labAnalysis = [];
            this.pagination = { };
            await this.getLabAnalysis();
        },

        changeAnalysisStatusMultipleItems(status) {
            const selectedAnalysis = this.selectedAnalysis.map(item => item.id);
            this.changeAnalysisStatus(selectedAnalysis, status);
        },

        async deleteAnalysis({ id }) {
            await this.$rq.deleteLabAnalysis(id);
            this.$toasted.global.toast_success({
                message: this.$t('laboratory.messages.delete'),
            });
            await this.getLabAnalysis();
        },
        provinceChanged(province) {
            this.queryParams.province = province;
            this.debouceLoadData();
        },
        componentChanged(componentType) {
            this.queryParams.components = componentType;
            this.debouceLoadData();
        },
        changeMultipleStatus(status) {
            const ids = this.selectedAnalysis.map(an => an.id);
            this.changeAnalysisStatus(ids, status);
            this.selectedAnalysis = [];
        },
        generateXML() {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('laboratory.xmlDownloadTitle'),
                payload: { msg: this.$t('laboratory.confirmationXML') },
                isForm: false,
                component: Vue.component('ConfirmForm', ConfirmForm),
                onConfirm: () => this.downloadFile(),
            });
        },
        async downloadFile() {
            const { data } = await this.$rq.downloadXMLFile();
            if (data) {
                await this.saveFileInDisk(data);
            }
        },
        async saveFileInDisk(content) {
            const { data } = await this.$rq.downloadFile(content.id);
            this.$utils.saveFileOnDisk(data, content.fileName);
            await this.getLabAnalysis();
        },
        async updateAnalysisInfo(item) {
            try {
                this.labAnalysis = [];
                this.queryParams = {};
                this.pagination = {};
                await this.$rq.updateLabAnalysisInfos(item.id);
                this.$toasted.global.toast_success({
                    message: this.$t('laboratory.sucess'),
                });
                await this.getLabAnalysis();
            } catch (error) {
                this.$root.$emit('showModal', {
                    title: this.$t('generic.error'),
                    isForm: false,
                    component: Vue.component('ErrorPopup', ConfirmForm),
                    payload: { msg: error.response.data.error.message || '' },
                });
            }
        },
        async handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 1,
            };

            this.pagination = {};
            this.labAnalysis = [];

            await this.debouceLoadData();
        },
        async handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
            };


            // reset filter if needed
            if (filterParams?.reset) {
                this.getFilterValues(true);
            }

            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;

            this.pagination = {};
            this.labAnalysis = [];
            await this.getLabAnalysis();
        },
        // Ce sera utilisé très prochainement dans une nouvelle story
        areItemsInSameType(type) {
            if (this.selectedAnalysis.length === 0) return false;
            return this.selectedAnalysis.every(analysis => analysis.status.key === type);
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getLabAnalysis();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
<style lang="scss" scoped>

    .date{
        min-width: 260px;
    }
    .btn-container{
        margin: 4px 8px;
    }
    .download-container{
        display: flex;
        justify-content: flex-end;
        align-items: baseline;
        .btn-dowload {
            border-radius: 6px;
        }
    }
     .address{
        min-width: 200px;
    }

</style>
